import { useContext, useEffect, useRef, useState } from 'react';
import { ProjectContext } from './Context';
import { filter, isEmpty, map } from 'lodash';
import { useQuery, useMutation } from '@apollo/client/react';
import { DEFAULT_PROJECT_STATE } from '@/components/Projects/CreateProjectModal/constants';
import { mapCodes } from '@/components/Projects/CreateProjectModal/helpers';
import { parseProjectShifts } from '@/components/Projects/CreateProjectModal/validation';
import {
  Event,
  Project,
  GetManagerAutocompleteOptionsDocument,
  GetProjectCodesDocument,
  CreateProjectDocument,
  DestroyProjectDocument,
  ProjectCreate,
  ClientRepInput,
  ManagerCreate,
  AddressInput,
  ActivityInput,
  GetCompanySettingsDocument,
} from '@/graphql/generated/graphql';
import { getSubProjectCodeInitials } from '@/utils/helperFunctions';
import { displayNotification } from '@/utils/notifications';
import CurrentUserContext from '../CurrentUser';
import { DEFAULT_PROJECT_SHIFTS } from '@/utils/constants';

const AUTO_SAVE_DELAY_MS = 800;

interface ProjectContextProviderProps {
  children: React.ReactNode;
  parentSource?: Event | Project;
  onProjectCreated?: () => void;
}

export type defaultCompanySettingsType = {
  enableWeekends: string;
  companyWorkShifts: string;
  companyDefaultCurrency: string;
};

const ProjectContextProvider = ({ children, parentSource, onProjectCreated }: ProjectContextProviderProps) => {
  const { currentUser } = useContext(CurrentUserContext);

  const { data: managerOptions } = useQuery(GetManagerAutocompleteOptionsDocument, {
    skip: !currentUser?.defaultCompany?.id,
  });

  const { data: companySettings } = useQuery(GetCompanySettingsDocument);
  const companyWorkShifts =
    companySettings?.getCompanySettings.find((setting) => setting.name === 'work-shifts')?.value ||
    DEFAULT_PROJECT_SHIFTS;
  const companyDefaultCurrency =
    companySettings?.getCompanySettings.find((setting) => setting.name === 'default-currency')?.value || '£';

  const defaultCompanySettings = {
    enableWeekends: 'false',
    companyWorkShifts: companyWorkShifts,
    companyDefaultCurrency: companyDefaultCurrency,
  };

  const { data: projectCodes } = useQuery(GetProjectCodesDocument, {
    skip: !currentUser?.defaultCompany?.id,
    variables: { companyId: currentUser?.defaultCompany?.id as string },
  });

  const [createProject] = useMutation(CreateProjectDocument);
  const [destroyProject] = useMutation(DestroyProjectDocument);

  const isProjectCodeUnique = (code: string) => !mapCodes(projectCodes?.getProjects).includes(code);

  const parentProject: Project | undefined = parentSource
    ? parentSource.__typename === 'Event'
      ? parentSource.project
      : (parentSource as Project)
    : undefined;

  // States
  const [project, setProject] = useState<ProjectCreate>(() => ({
    id: '',
    ...DEFAULT_PROJECT_STATE,
    ...(parentSource &&
      parentProject?.client && {
        clientId: parentProject?.client.id,
        ...(parentProject?.pointOfContacts && {
          clientReps: parentProject?.pointOfContacts?.map((pc) => {
            return {
              name: pc.companyResource?.name ?? '',
              email: pc.companyResource?.resourceMeta?.email ?? '',
              phone: pc.companyResource?.resourceMeta?.primaryPhone ?? '',
            };
          }),
        }),
        projectUsers: map(parentProject?.managers || [], (man) => ({
          companyResourceId: man.companyResource?.id,
          employmentId: man.employment?.id,
          userId: man.user?.id,
          title: man.title,
        })),
      }),
  }));

  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const autoSavedSnapshotRef = useRef<string>('');
  const handleCreateProjectRef = useRef<(options?: { silent?: boolean; onSuccess?: () => void }) => void>(() => {});

  const filteredProjectSettings = () => {
    const oldSettings = project.settings || [];
    const parsedShifts = parseProjectShifts(oldSettings);

    if (parsedShifts.length > 0) {
      const updatedShifts = JSON.stringify(filter(parsedShifts, (shift) => !isEmpty(shift.name)));
      return [
        ...filter(oldSettings, (setting) => setting.name !== 'project-shifts'),
        { name: 'project-shifts', value: updatedShifts },
      ];
    }

    return oldSettings;
  };

  const hasCharAfterTilde = (str: string) => /~.+/.test(str);

  const handleCreateProject = (options?: { silent?: boolean; onSuccess?: () => void }) => {
    const { silent = false, onSuccess } = options ?? {};
    const isUpdate = Boolean(project.id);
    const newCode = parentProject ? `${getSubProjectCodeInitials(parentProject.code)}${project.code}` : project.code;

    if (!newCode) return;

    if (newCode.includes('~') && !hasCharAfterTilde(newCode)) return;

    setIsSaving(true);

    createProject({
      variables: {
        id: project.id,
        companyId: currentUser?.defaultCompany?.id as string,
        clientId: parentProject ? (parentProject.client?.id as string) : project.clientId,
        title: project.title,
        code: newCode,
        description: project.description,
        clientReps: project.clientReps?.filter((rep) => rep.name && rep.name.trim() !== '') as ClientRepInput[],
        status: 'Not Started',
        addresses: project.addresses,
        sourceType: parentSource?.__typename as string,
        sourceId: parentSource?.id as string,
        projectUsers: project.projectUsers,
        tagIds: project.tagIds,
        contractTypeIds: project.contractTypeIds,
        activities: project.activities,
        settings: filteredProjectSettings(),
        visible: !silent,
      },
      onCompleted: (res) => {
        setProject((prev) => ({ ...prev, id: res.createProject as string }));
        setIsSaving(false);
        setLastSaved(new Date());
        if (!silent) {
          onProjectCreated?.();
          displayNotification({
            type: 'success',
            text: isUpdate ? 'Project Updated Successfully' : 'Project Created Successfully',
          });
        }
        onSuccess?.();
      },
      onError: (err) => {
        setIsSaving(false);
        if (!silent) {
          displayNotification({
            type: 'error',
            text: err.message ?? 'Unable to create project, try again later.',
          });
        }
      },
      refetchQueries: silent ? [] : ['getProjects'],
    });
  };

  const handleResetProject = (onDone?: () => void) => {
    const idToDelete = project.id;
    setProject({ ...DEFAULT_PROJECT_STATE, id: '' });
    autoSavedSnapshotRef.current = '';
    setResetKey((k) => k + 1);

    if (idToDelete) {
      destroyProject({
        variables: { id: idToDelete },
        refetchQueries: ['getProjects'],
      });
    }

    onDone?.();
  };

  // Keep ref in sync so the auto-save effect always calls the latest version
  useEffect(() => {
    handleCreateProjectRef.current = handleCreateProject;
  });

  // Auto-save: fires after any project field change once the minimum data is present.
  // All draft persistence goes through here silently; only the explicit
  // "Create Project" action produces a notification and list refetch.
  useEffect(() => {
    //eslint-disable-next-line
    const { id, ...rest } = project;
    const snapshot = JSON.stringify(rest);

    if (snapshot === autoSavedSnapshotRef.current) return;

    const newCode = parentProject ? `${getSubProjectCodeInitials(parentProject.code)}${project.code}` : project.code;
    if (!newCode || !project.title) return;

    const timer = setTimeout(() => {
      autoSavedSnapshotRef.current = snapshot;
      handleCreateProjectRef.current({ silent: true });
    }, AUTO_SAVE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [project]);

  const onProjectDataChange = (key: string, value: string | ManagerCreate[]) => {
    setProject((prev) => ({ ...prev, [key]: value }));
  };

  const onAddressesChange = (addresses: AddressInput[]) => {
    setProject((prev) => ({
      ...prev,
      addresses: addresses,
    }));
  };

  const onTagsChange = (selectedTags: string[]) => {
    setProject((prev) => ({
      ...prev,
      tagIds: selectedTags,
    }));
  };

  const onContractChange = (contractIds: string[]) => {
    setProject((prev) => ({
      ...prev,
      contractTypeIds: contractIds,
    }));
  };

  const onActivitiesChange = (activities: ActivityInput[]) => {
    setProject((prev) => ({
      ...prev,
      activities,
    }));
  };

  const onSettingsChange = (newSettings: { name: string; value: string }[]) => {
    setProject((prev) => ({
      ...prev,
      settings: newSettings,
    }));
  };

  const onClientRepChange = (index: number, field: string, value: string) => {
    setProject((prevProject) => {
      const updatedReps = [...(prevProject.clientReps || [])];
      if (updatedReps[index]) {
        updatedReps[index] = {
          ...updatedReps[index],
          [field]: value,
        };
      }
      return {
        ...prevProject,
        clientReps: updatedReps,
      };
    });
  };

  const onClientRepRemove = (index: number) => {
    setProject((prevProject) => {
      const updatedReps = (prevProject.clientReps || []).filter((_, i) => i !== index);
      return {
        ...prevProject,
        clientReps: updatedReps,
      };
    });
  };

  const onAddClientRep = () => {
    setProject((prevProject) => {
      const newRep = {
        name: '',
        email: '',
        phone: '',
      };
      return {
        ...prevProject,
        clientReps: [...(prevProject.clientReps || []), newRep],
      };
    });
  };

  const onManagerTitleChange = (manager: ManagerCreate, title: string) => {
    setProject((prev) => ({
      ...prev,
      projectUsers: map(prev.projectUsers || [], (m) =>
        (m.employmentId && m.employmentId === manager.employmentId) ||
        (m.userId && m.userId === manager.userId) ||
        (m.companyResourceId && m.companyResourceId === manager.companyResourceId)
          ? { ...m, title }
          : m,
      ),
    }));
  };

  const getManagerData = (managerId?: string | null) => {
    const list = managerOptions?.getManagerAutocompleteOptions || [];
    const manager = list.find((man) => man.id === managerId || man.user?.id === managerId);
    return (manager ?? { id: managerId ?? '', name: '' }) as any;
  };

  const context = {
    mode: 'create',
    parentProject,
    project,
    setProject,
    loadingProject: false,
    managers: (managerOptions?.getManagerAutocompleteOptions || []) as any[],
    isProjectCodeUnique,
    isSaving,
    lastSaved,
    handleCreateProject,
    onProjectDataChange,
    onAddressesChange,
    onTagsChange,
    onContractChange,
    onActivitiesChange,
    onSettingsChange,
    onClientRepChange,
    onManagerTitleChange,
    getManagerData,
    onClientRepRemove,
    onAddClientRep,
    handleResetProject,
    resetKey,
    defaultCompanySettings,
  };

  return <ProjectContext.Provider value={context}>{children}</ProjectContext.Provider>;
};
export default ProjectContextProvider;
